import Executive from '../../framework/rmi-util'
import { register } from '../../framework/rmi-server-util'
import { CheckPolicy, Resource } from '../../framework/permission-util'
import { RT } from '../permissions'
import { Invitation } from './invitation.entities'
import { invitationTechnical } from './InvitationTechnical'
import { InvitationRole } from '../../../../shared/types/account-types'


type CreateInvitationRequest = {
	clientId: string
	inviterServiceProviderId: string
	inviterUserId: string
	firstName?: string
	lastName?: string
	email: string
	preferredLocale: string
	role: InvitationRole
	expiresInHours?: number
}

type UpdateInvitationRequest = {
	invitationId: string
	firstName?: string
	lastName?: string
	email?: string
	preferredLocale?: string
	role?: InvitationRole
}

type UpdateUserRequest = {
	invitationId: string
	firstName?: string
	lastName?: string
	preferredLocale?: string
}

type InvitationResponse = Invitation

export default class InvitationExecutive extends Executive {
	initServer() {}

	@CheckPolicy()
	async createAndSendInvitation(@Resource(RT.PROVIDER) inviterServiceProviderId: string, request: CreateInvitationRequest): Promise<InvitationResponse> {
		// Ensure the inviterServiceProviderId matches the one in the request
		if (request.inviterServiceProviderId !== inviterServiceProviderId) {
			throw new Error('Inviter service provider ID mismatch')
		}
		return invitationTechnical.createAndSend(request as any)
	}

	@CheckPolicy()
	async resendInvitation(@Resource(RT.INVITATION) invitationId: string): Promise<void> {
		await invitationTechnical.resend(invitationId)
	}

	@CheckPolicy()
	async updateInvitation(@Resource(RT.INVITATION) invitationId: string, request: UpdateInvitationRequest): Promise<InvitationResponse> {
		// Ensure the invitationId matches the one in the request
		if (request.invitationId !== invitationId) {
			throw new Error('Invitation ID mismatch')
		}
		return invitationTechnical.updateAndResend(request.invitationId, {
			firstName: request.firstName,
			lastName: request.lastName,
			email: request.email,
			preferredLocale: request.preferredLocale,
			role: request.role
		})
	}

	@CheckPolicy()
	async cancelInvitation(@Resource(RT.INVITATION) invitationId: string): Promise<void> {
		await invitationTechnical.cancel(invitationId)
	}

	// Resolve token to prefill the registration
	async resolveInvitationToken(token: string): Promise<{ email: string, role: string, preferredLocale: string, inviteId: string }> {
		return invitationTechnical.resolveToken(token)
	}

	// Accept invitation and create user + external records
	async acceptInvitation(payload: { token: string, password: string, preferredLocale?: string }): Promise<{ userId: string, b2bPartnerUserId?: string, peakCustomerId?: string }> {
		return invitationTechnical.accept(payload.token, payload.password, payload.preferredLocale)
	}

	@CheckPolicy()
	async getInvitations(@Resource(RT.CLIENT) clientId: string, @Resource(RT.PROVIDER) inviterServiceProviderId: string): Promise<Invitation[]> {
		return invitationTechnical.getInvitations(clientId, inviterServiceProviderId)
	}

	@CheckPolicy()
	async deleteInvitation(@Resource(RT.INVITATION) invitationId: string): Promise<void> {
		return invitationTechnical.deleteInvitation(invitationId)
	}

	@CheckPolicy()
	async deleteUser(@Resource(RT.INVITATION) invitationId: string): Promise<{
		success: boolean
		deletedMyServicesUser?: boolean
		deletedPeakUser?: boolean
		deletedInvitation?: boolean
		errors?: string[]
	}> {
		return invitationTechnical.deleteUser(invitationId)
	}

	@CheckPolicy()
	async updateUser(@Resource(RT.INVITATION) invitationId: string, request: UpdateUserRequest): Promise<{ success: boolean, errors: string[] }> {
		// Ensure the invitationId matches the one in the request
		if (request.invitationId !== invitationId) {
			throw new Error('Invitation ID mismatch')
		}
		return invitationTechnical.updateUser(request.invitationId, {
			firstName: request.firstName,
			lastName: request.lastName,
			preferredLocale: request.preferredLocale,
		})
	}

	async checkEmailAvailability(email: string, clientId: string): Promise<{
		available: boolean
		status?: string
		invitationId?: string
		message: string
	}> {
		return invitationTechnical.checkEmailAvailability(email, clientId)
	}

	async createFakeMainUserInvitation(
		clientId: string,
		serviceProviderId: string,
		email: string,
		firstName: string,
		lastName: string,
		language: string,
		b2bPartnerUserId: string,
		peakCustomerId: string
	): Promise<void> {
		return invitationTechnical.createFakeMainUserInvitation(
			clientId,
			serviceProviderId,
			email,
			firstName,
			lastName,
			language,
			b2bPartnerUserId,
			peakCustomerId
		)
	}

	@CheckPolicy()
	async updateInvitationRole(@Resource(RT.INVITATION) invitationId: string, newRole: InvitationRole): Promise<InvitationResponse> {
		return invitationTechnical.updateInvitationRole(invitationId, newRole)
	}
}

register(InvitationExecutive)


